import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs, redirect } from 'react-router';
import { Device_CommissioningState } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { SystemPermission_Permission } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/user_pb';
import { DeviceDetailsModel } from '~/shared/models/system/DeviceDetailsModel';
import { listMetricsQuery } from '~/shared/api/metrics.queries';
import { getSystemQuery } from '~/shared/api/system.queries';
import {
  checkAuthorization,
  checkIfUserHasPermissionForSystem,
} from '~/shared/auth/auth-utils';
import { MetricsModel } from '~/shared/models/metrics/MetricsModel';
import { assertIsDefined } from '~/types/assert-type';
import { SystemModel } from '~/shared/models/system/SystemModel';
import { linkDevicePeripheralOptionsQuery } from '~/pages/System/api/linkDevicePeripheralOptions.queries';
import { LinkDevicePeripheralOptionsModel } from '~/pages/System/models/LinkDevicePeripheralOptionsModel';

type SensorReadingsLoaderData = {
  metrics?: MetricsModel;
  deviceDetails?: DeviceDetailsModel;
  linkDevicePeripheralOptions?: LinkDevicePeripheralOptionsModel;
};

export const sensorReadingsLoader =
  (queryClient: QueryClient) =>
  async ({
    params,
    request,
  }: LoaderFunctionArgs): Promise<SensorReadingsLoaderData | Response> => {
    await checkAuthorization(queryClient, request);
    const { productId, systemId } = params;

    assertIsDefined(productId);
    assertIsDefined(systemId);

    const system = await queryClient.ensureQueryData(getSystemQuery(systemId));

    const systemModel = new SystemModel({ system });

    const product = systemModel.productById(productId);

    if (!product) {
      return redirect('..');
    }

    const deviceNeedsPeripheralLinking =
      product.device.commissioningState ===
      Device_CommissioningState.PERIPHERAL_LINKING;

    const shouldAllowCommissioning = await checkIfUserHasPermissionForSystem(
      queryClient,
      request,
      systemId,
      [
        SystemPermission_Permission.ADMIN,
        SystemPermission_Permission.COMMISSIONING,
      ],
    );

    return {
      metrics: await queryClient.ensureQueryData(
        listMetricsQuery({
          targets: product.availableMetricTargets,
        }),
      ),
      linkDevicePeripheralOptions:
        deviceNeedsPeripheralLinking && shouldAllowCommissioning
          ? await queryClient.ensureQueryData(
              linkDevicePeripheralOptionsQuery({
                deviceName: product.device.name,
              }),
            )
          : undefined,
    };
  };
